import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { faCalendarCheck, faLocationDot, faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import OccurrenceVideoModal from '../dispatch/occurrenceForm/OccurrenceVideoModal';
import { appAgentService } from '../../services/central-api/app-agent';
import { WindowInformation } from '../../services/window-information';
import DragAndZoom from '../DragAndZoom';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Card from '../Card';
import { RootNavigation } from '../../Routes';
import { openInNewTab } from '../../services/navigation-assistant';

export function AreaInvasionOccurrence({ occurrence, navigation }: { occurrence: Occurrence; navigation: RootNavigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isModalVisible, setModalVisible] = useState(false);
    const windowInfo = WindowInformation();

    if (occurrence.triggerType !== 'area_invasion') {
        return <></>;
    }

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);

            for (const detection of occurrence.resume.detections) {
                ctx.strokeStyle = 'red';
                ctx.lineWidth = 4;
                ctx.rect(detection.objectRect.x, detection.objectRect.y, detection.objectRect.width, detection.objectRect.height);
                ctx.stroke();

                if (detection.regionPoints.length > 0) {
                    ctx.strokeStyle = 'yellow';
                    ctx.lineWidth = 4;
                    ctx.beginPath();
                    ctx.setLineDash([15, 15]);
                    ctx.moveTo(detection.regionPoints[0].x, detection.regionPoints[0].y);
                    for (let i = 1; i < detection.regionPoints.length; i++) {
                        ctx.lineTo(detection.regionPoints[i].x, detection.regionPoints[i].y);
                    }
                    ctx.closePath();
                    ctx.stroke();
                }
            }
        };

        image.src = occurrence.resume.imageUrl;
    });

    return (
        <View style={{ gap: 20 }}>
            <View style={{ gap: 5 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TouchableOpacity
                            onPress={() => {
                                if (occurrence?.id) {
                                    openInNewTab('Dispatch', { screen: 'DispatchOccurrenceDetail', params: { occurrenceId: occurrence.id } });
                                }
                            }}
                            disabled={windowInfo.isMobile}
                        >
                            <MyAppText style={{ fontSize: 16, color: theme.colors.labelColor, fontWeight: 'bold' }}>
                                {translate('nProtocol')}: {occurrence?.id}
                            </MyAppText>
                        </TouchableOpacity>
                        <MyAppText> </MyAppText>
                        <MyAppText style={{ fontSize: 14, color: theme.colors.card.detailText }}>
                            ({translate('team')} {translate(occurrence.OccurrenceUnits?.length && occurrence.OccurrenceUnits[0].OccurrenceUnit.isStarter ? 'starter' : 'support').toLowerCase()})
                        </MyAppText>
                    </View>
                    <TouchableOpacity onPress={() => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' })}>
                        <FontAwesomeIcon color={theme.colors.textColor} fontSize={30} icon={faTimes} />
                    </TouchableOpacity>
                </View>
                <MyAppText style={styles.alertText}>
                    {translate('areaInvasionAlert')}:
                </MyAppText>
                <DragAndZoom>
                    <canvas style={{ width: '100%' }} ref={canvasRef}></canvas>
                </DragAndZoom>
            </View>
            <Card>
                <View style={{ gap: 15 }}>
                    <View style={styles.visualizedRow} >
                        <FontAwesomeIcon icon={faPlay} style={{ width: 22 }} color={theme.colors.iconColor} />
                        <TouchableOpacity onPress={() => setModalVisible(true)}>
                            <MyAppText style={styles.watchVideoText}>
                                {translate('watchVideo')}
                            </MyAppText>
                        </TouchableOpacity>
                    </View>
                    <View style={{ gap: 5 }}>
                        <MyAppText style={styles.cardBoldText}>{translate('visualizedOn')}</MyAppText>
                        <View style={styles.visualizedRow}>
                            <FontAwesomeIcon icon={faLocationDot} style={{ color: theme.colors.iconColor }} />
                            <MyAppText style={styles.cardText}>{occurrence.externalReference?.title}</MyAppText>
                        </View>
                        <View style={styles.visualizedRow}>
                            <FontAwesomeIcon icon={faCalendarCheck} style={{ color: theme.colors.iconColor }} />
                            <MyAppText style={styles.cardText}>{moment(occurrence.resume.time).format('DD/MM/YYYY HH:mm:ss')}</MyAppText>
                        </View>
                    </View>
                </View>
                <View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('occurrenceId')}:</MyAppText>
                        <MyAppText style={styles.cardText}>{occurrence.id}</MyAppText>
                    </View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('priority')}:</MyAppText>
                        <MyAppText style={[styles.cardText, occurrence.priority === 'maximum' ? { color: '#FF0027' } : undefined]}>{occurrence.priority ? translate(occurrence.priority) : ''}</MyAppText>
                    </View>
                    <View>
                        <View style={{ paddingBottom: 30 }}>
                            <MyAppText style={styles.cardBoldText}>{translate('narrative')}:</MyAppText>
                            <MyAppText style={styles.cardText}>{occurrence.narrative}</MyAppText>
                        </View>
                    </View>
                </View>
            </Card>
            <OccurrenceVideoModal
                occurrenceId={occurrence.id || 0}
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                occurrenceTime={new Date(occurrence.resume.time).getTime()}
                apiService={appAgentService}
            />
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    alertText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    alertName: {
        color: theme.colors.textColor,
        fontSize: 18,
        fontWeight: 'bold',
        paddingBottom: 8
    },
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
    cardText: {
        color: theme.colors.iconColor,
        fontSize: 12
    },
    cardBoldText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    plateInfoText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 24
    },
    watchVideoText: {
        color: theme.colors.iconColor,
        fontSize: 16,
        textDecorationLine: 'underline'
    }
}));
